import { SubscriptionType } from '@sc/graphql';
import { ComponentType, SVGProps } from 'react';
import { useUser } from '../providers/users';
import { BillingLite } from '../components/UpgradeLite';
import { Modal } from '@sc/shared-ui';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

type Props = {
  showInModal?: boolean;
  upgradePrompt: string;
  subscriptionType: SubscriptionType | SubscriptionType[];
  Icon?: IconComponent;
};

function assertsSubscription<
  P extends object & { open: boolean; onClose: () => void }
>(
  WrappedComponent: ComponentType<P>,
  options: {
    showInModal: true;
    upgradePrompt: string;
    subscriptionType: SubscriptionType | SubscriptionType[];
    Icon?: IconComponent;
  }
): ComponentType<P>;

function assertsSubscription<
  P extends object & { open?: boolean; onClose?: () => void }
>(
  WrappedComponent: ComponentType<P>,
  options: {
    showInModal?: false;
    upgradePrompt: string;
    subscriptionType: SubscriptionType | SubscriptionType[];
    Icon?: IconComponent;
  }
): ComponentType<P>;

function assertsSubscription<
  P extends object & { open: boolean; onClose: () => void }
>(
  WrappedComponent: ComponentType<P>,
  { showInModal, upgradePrompt, subscriptionType, Icon }: Props
): ComponentType<P> {
  return ({ open, onClose, ...rest }: P) => {
    const { user } = useUser();

    const checkAgainstSubscriptions = Array.isArray(subscriptionType)
      ? subscriptionType
      : [subscriptionType];

    const [firstSubscription] = checkAgainstSubscriptions;

    if (!firstSubscription) throw new Error('No subscription found');

    if (
      !user.company.subscription?.type ||
      !checkAgainstSubscriptions.includes(user.company.subscription.type)
    ) {
      if (showInModal) {
        return (
          <Modal
            hideTopHr
            open={open}
            onClose={onClose}
            dialogPanelClassname="w-120"
          >
            <BillingLite
              subscriptionType={firstSubscription}
              upgradePrompt={upgradePrompt}
              Icon={Icon}
              modal
            />
          </Modal>
        );
      }

      return (
        <div className="bg-background-tertiary w-110 border border-grey-t-800 rounded-xs overflow-hidden">
          <BillingLite
            subscriptionType={firstSubscription}
            upgradePrompt={upgradePrompt}
            Icon={Icon}
          />
        </div>
      );
    }

    return <WrappedComponent {...({ ...rest, onClose, open } as P)} />;
  };
}

export default assertsSubscription;
